import { useEffect, useMemo, useRef, useState } from 'react'
import orderBy from 'lodash/orderBy'
import { useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import SearchInput from '../SearchInput'
import RefreshButton from '../buttons/RefreshButton'
import IconButton from '../buttons/IconButton'
import DataTable from '../DataTable'
import Renders from '../DataTable/renders'
import MikeSlidingPanel from '../SlidingPanel'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import { useTypedSelector } from '../../reducers'
import { StateEnum } from '../../model/StateEnum'
import ISystemStatusMessage, {
  Severity
} from '../../model/ISystemStatusMessage'
import {
  createStatusMessage,
  deleteStatusMessage,
  loadStatusMessages,
  updateStatusMessage
} from '../../actions/systemStatus'
import {
  deriveStatus,
  statusMessageSortKey,
  StatusMessageSortField
} from '../../utils/systemStatus'
import { formatUtcDateTime } from '../../utils/systemStatusFormat'
import SeverityChip from './SeverityChip'
import StatusChip from './StatusChip'
import SystemStatusMessageForm, {
  StatusMessageFormValues
} from './SystemStatusMessageForm'

const ROWS_PER_PAGE = 10
// Re-derive row statuses on this cadence so a message flips to Expired without
// a manual refresh (mirrors the banner's tick).
const STATUS_TICK_MS = 30_000

// Table cell text: full colour for live rows, muted for expired ones.
const CELL_TEXT = '#123B52'
const CELL_TEXT_MUTED = '#8B9AA5'

const DateCell = ({ value, expired }: { value: string; expired: boolean }) => (
  <Box
    sx={{
      fontSize: 13.5,
      whiteSpace: 'nowrap',
      color: expired ? CELL_TEXT_MUTED : CELL_TEXT
    }}
  >
    {formatUtcDateTime(value)}
  </Box>
)

const StyledHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 0.5),
  backgroundColor: '#f2f5f7'
}))

const StyledToolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3),
  backgroundColor: '#f2f5f7'
}))

const emptyForm = (): StatusMessageFormValues => ({
  type: Severity.Informational,
  title: '',
  body: '',
  startDate: new Date().toISOString(),
  expiringDate: new Date(Date.now() + 60 * 60 * 1000).toISOString()
})

const SystemStatusMessages = () => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const messages = useTypedSelector(state => state.systemStatus.adminMessages)
  const loading = useTypedSelector(
    state => state.systemStatus.adminLoadingState === StateEnum.LOADING
  )
  const saving = useTypedSelector(state => state.systemStatus.saving)

  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<StatusMessageSortField>('type')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showExpired, setShowExpired] = useState(false)
  const [page, setPage] = useState(0)
  const [panelOpen, setPanelOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formValues, setFormValues] = useState<StatusMessageFormValues>(
    emptyForm
  )
  const [deleteTarget, setDeleteTarget] = useState<ISystemStatusMessage | null>(
    null
  )

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    dispatch(loadStatusMessages())
  }, [dispatch])

  // Tick so derived statuses (Active/Pending/Expired) update as time passes,
  // keeping the table in sync with the banner without a manual refresh.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), STATUS_TICK_MS)
    return () => window.clearInterval(tick)
  }, [])

  // Close the panel when a save completes (saving true → false). Note: on a
  // save error this still closes; the error surfaces via the app error handler.
  const prevSaving = useRef(false)
  useEffect(() => {
    if (prevSaving.current && !saving) {
      setPanelOpen(false)
    }
    prevSaving.current = saving
  }, [saving])

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const rows = messages.filter(m => {
      if (!showExpired && deriveStatus(m, now) === 'Expired') {
        return false
      }
      if (!needle) {
        return true
      }
      return (
        m.title.toLowerCase().includes(needle) ||
        m.body.toLowerCase().includes(needle)
      )
    })
    return orderBy(
      rows,
      [statusMessageSortKey(sortBy, now), m => new Date(m.startDate).getTime()],
      [sortOrder, 'asc']
    )
  }, [messages, search, showExpired, now, sortBy, sortOrder])

  const paginated = useMemo(
    () =>
      filtered.slice(
        page * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE + ROWS_PER_PAGE
      ),
    [filtered, page]
  )

  const openCreate = () => {
    setEditingId(null)
    setFormValues(emptyForm())
    setPanelOpen(true)
  }

  const openEdit = (row: ISystemStatusMessage) => {
    setEditingId(row.id)
    setFormValues({
      type: row.type,
      title: row.title,
      body: row.body,
      startDate: row.startDate,
      expiringDate: row.expiringDate
    })
    setPanelOpen(true)
  }

  const handleSubmit = (values: StatusMessageFormValues) => {
    if (editingId === null) {
      dispatch(createStatusMessage(values))
    } else {
      dispatch(updateStatusMessage(editingId, values))
    }
  }

  const confirmDelete = () => {
    if (deleteTarget) {
      dispatch(deleteStatusMessage(deleteTarget.id))
      setDeleteTarget(null)
    }
  }

  const isExpired = (row: ISystemStatusMessage) =>
    deriveStatus(row, now) === 'Expired'

  const columns = [
    {
      field: 'type',
      label: intl.formatMessage({ id: 'systemStatus.table.severity' }),
      render: (_v: unknown, row: ISystemStatusMessage) => (
        <SeverityChip severity={row.type} muted={isExpired(row)} />
      )
    },
    {
      field: 'title',
      label: intl.formatMessage({ id: 'systemStatus.table.title' }),
      render: (_v: unknown, row: ISystemStatusMessage) => (
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: row.id === editingId ? 500 : 400,
            color: isExpired(row) ? CELL_TEXT_MUTED : CELL_TEXT,
            whiteSpace: 'normal'
          }}
        >
          {row.title}
        </Typography>
      )
    },
    {
      field: 'status',
      label: intl.formatMessage({ id: 'systemStatus.table.status' }),
      render: (_v: unknown, row: ISystemStatusMessage) => (
        <StatusChip status={deriveStatus(row, now)} />
      )
    },
    {
      field: 'startDate',
      label: intl.formatMessage({ id: 'systemStatus.table.start' }),
      render: (_v: unknown, row: ISystemStatusMessage) => (
        <DateCell value={row.startDate} expired={isExpired(row)} />
      )
    },
    {
      field: 'expiringDate',
      label: intl.formatMessage({ id: 'systemStatus.table.expiry' }),
      render: (_v: unknown, row: ISystemStatusMessage) => (
        <DateCell value={row.expiringDate} expired={isExpired(row)} />
      )
    }
  ]

  const actions = [
    {
      name: 'Edit',
      callBack: openEdit,
      render: Renders.renderEdit,
      disableGutters: true
    },
    {
      name: 'Delete',
      callBack: (row: ISystemStatusMessage) => setDeleteTarget(row),
      render: Renders.renderDelete,
      disableGutters: true
    }
  ]

  return (
    <>
      <StyledHeader>
        <Typography sx={{ fontWeight: 500, fontSize: 20, color: '#0B4566' }}>
          {intl.formatMessage({ id: 'systemStatus.page.title' })}
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#5A7383', mt: '4px' }}>
          {intl.formatMessage({ id: 'systemStatus.page.subtitle' })}
        </Typography>
      </StyledHeader>
      <StyledToolbar>
        <SearchInput
          id="system-status-search-input"
          placeholder={intl.formatMessage({
            id: 'systemStatus.toolbar.search'
          })}
          autoFocus
          text={search}
          onTextChange={(text: string) => {
            setSearch(text)
            setPage(0)
          }}
        />
        <RefreshButton onClick={() => dispatch(loadStatusMessages())} />
        <FormControlLabel
          // Match the toolbar buttons' 3rem height so the switch centres in
          // line with the Refresh button beside it (toolbar is flex-end).
          sx={{ ml: 1, mr: 0, height: '3rem' }}
          control={
            <Switch
              color="secondary"
              checked={showExpired}
              onChange={(_e, checked) => {
                setShowExpired(checked)
                setPage(0)
              }}
            />
          }
          label={
            <>
              <Typography
                sx={{
                  fontWeight: '500',
                  letterSpacing: '0.1px',
                  textTransform: 'none',
                  color: '#00A4EC !important',
                  fontSize: '0.875rem !important'
                }}
              >
                <FormattedMessage id="systemStatus.toolbar.showExpired" />
              </Typography>
            </>
          }
        />
        <Box sx={{ ml: 'auto' }}>
          <IconButton
            messageId="systemStatus.toolbar.add"
            onClick={openCreate}
          />
        </Box>
      </StyledToolbar>

      <DataTable
        loading={loading}
        hasSelection={false}
        idField="id"
        columns={columns}
        actions={actions}
        data={paginated}
        page={page}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
        totalCount={filtered.length}
        onChangePage={(newPage: number) => setPage(newPage)}
        onHandleRequestSort={(field: StatusMessageSortField, order: string) => {
          setSortBy(field)
          setSortOrder(order === 'asc' ? 'asc' : 'desc')
          setPage(0)
        }}
        _order={[sortOrder]}
        _orderBy={[sortBy]}
      />

      <MikeSlidingPanel
        position="right"
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        titleArea={
          <Typography sx={{ fontWeight: 500, fontSize: 16, color: '#0B4566' }}>
            {intl.formatMessage({
              id:
                editingId === null
                  ? 'systemStatus.form.createTitle'
                  : 'systemStatus.form.editTitle'
            })}
          </Typography>
        }
        contentArea={
          panelOpen ? (
            <SystemStatusMessageForm
              initialValues={formValues}
              editing={editingId !== null}
              saving={saving}
              onSubmit={handleSubmit}
              onCancel={() => setPanelOpen(false)}
            />
          ) : null
        }
        noGrayOverlay={false}
      />

      <ConfirmationDialog
        open={deleteTarget !== null}
        title={intl.formatMessage({ id: 'systemStatus.delete.title' })}
        message={
          deleteTarget
            ? intl.formatMessage(
                { id: 'systemStatus.delete.body' },
                { title: deleteTarget.title }
              )
            : ''
        }
        ok={intl.formatMessage({ id: 'systemStatus.delete.confirm' })}
        onOk={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  )
}

export default SystemStatusMessages
